import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { DocumentModel, DocumentPreviewModel } from '@app/data-layer/models/documents';
import { EntrySummaryStateService, EsOcrQuickFixService } from '@shared/es-mutual/services';
import { ReplaySubject } from 'rxjs';
import { EsOcrQuickFixMainShipmentDetailsComponent } from '../es-ocr-quick-fix-main-shipment-details/es-ocr-quick-fix-main-shipment-details.component';
import { EntrySummaryModel, LineModel } from '@app/data-layer/models/entry-summaries';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { HtsLineStatusHelper } from '@shared/hts-assistant/helpers/hts-line-status.helper';
import { enumEntrySummaryQuickFixStep } from '@shared/es-mutual/enums';
import { finalize, switchMap, take, takeUntil } from 'rxjs/operators';
import { enumEntrySummarySubStep } from '@app/modules/entry-summary/enums';
import { enumDocumentType } from '@app/data-layer/enums/documents';
import { DocumentsService } from '@services/documents.service';
import { EsOcrQuickFixBlDetailsComponent } from '../es-ocr-quick-fix-bl-details/es-ocr-quick-fix-bl-details.component';
import { EntrySummaryActionsHelper } from '@helpers/entry-summary-actions.helper';
import { EsOcrQuickFixSellerConsigneeDetailsComponent } from '../es-ocr-quick-fix-seller-consignee-details/es-ocr-quick-fix-seller-consignee-details.component';
import { EsOcrQuickFixShippingTosComponent } from '../es-ocr-quick-fix-shipping-tos/es-ocr-quick-fix-shipping-tos.component';
import { EsOcrQuickFixInvoiceMainDetailsComponent } from '../es-ocr-quick-fix-invoice-main-details/es-ocr-quick-fix-invoice-main-details.component';
import { EsOcrQuickFixManufacturerDetailsComponent } from '../es-ocr-quick-fix-manufacturer-details/es-ocr-quick-fix-manufacturer-details.component';
import { EsOcrQuickFixLineItemDetailsComponent } from '../es-ocr-quick-fix-line-item-details/es-ocr-quick-fix-line-item-details.component';
import { QuickFixStepModel } from '@shared/es-mutual/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { EsOcrQuickFixShipmentTypeDetailsComponent } from '../es-ocr-quick-fix-shipment-type-details/es-ocr-quick-fix-shipment-type-details.component';
import { CompaniesService } from '@services/companies.service';

@Component({
  selector: 'app-es-ocr-quick-fix-sidebar',
  templateUrl: './es-ocr-quick-fix-sidebar.component.html',
  styleUrls: ['./es-ocr-quick-fix-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsOcrQuickFixSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public entryDocuments: DocumentModel[];
  @ViewChild('stepForm', { read: ViewContainerRef, static: false }) public stepForm: ViewContainerRef;

  public previewModalDocs: DocumentPreviewModel[] = [];
  public isOpenDocsPreviewModal: boolean = false;
  public currentPreviewModalDocIndex: number = 0;
  public currentStepProgress: string;

  private stepsToFix: QuickFixStepModel[] = [];
  private currentStepIndex: number = 0;
  private sidebarFormRef: ComponentRef<any> = null;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private injector: Injector,
    private cdr: ChangeDetectorRef,
    private documentsService: DocumentsService,
    private companiesService: CompaniesService,
    private translateService: TranslateService,
    private esOcrQuickFixService: EsOcrQuickFixService,
    private entrySummaryStateService: EntrySummaryStateService,
  ) {}

  ngOnInit(): void {
    this.setSteps();
  }

  ngAfterViewInit(): void {
    this.previewModalDocs = this.esOcrQuickFixService.getCurrentPreviewModalDocs();

    this.setCurrentStepProgress(this.currentStepIndex);
    this.syncStepForm();

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }

  public onNext() {
    // #12863 — the "New line item" step saves only through its own Add button; Next/Finish just moves on.
    if (this.isNewLineItemStep) {
      this.Next();
      return;
    }

    this.sidebarFormRef.instance.onSubmit();
  }

  public onBack() {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
      this.setCurrentStepProgress(this.currentStepIndex);
      const updatedStep: QuickFixStepModel = this.stepsToFix[this.currentStepIndex];
      this.esOcrQuickFixService.setCurrentStep(updatedStep);

      this.syncStepForm();

      this.cdr.markForCheck();
    } else {
      this.esOcrQuickFixService.closeSidebar();
    }
  }

  public onPreviewDocument() {
    this.setIsOpenDocsPreviewModal(true);
  }

  public onCloseDocumentPreview() {
    this.setIsOpenDocsPreviewModal(false);
  }

  public onPreviewDocumentDelete(data: { docId: number; shipmentId: number }) {
    const { docId, shipmentId } = data;

    const doc = this.entryDocuments.find((doc) => doc.id === docId);
    if (!doc) {
      return;
    }

    this.deleteDocument(doc, shipmentId);
  }

  public deleteDocument(doc: DocumentModel, shipmentId: number): void {
    if (this.entrySummary?.id) {
      this.deleteDocumentFromServer(doc, shipmentId);
      return;
    }
  }

  public createMainShipmentDetailsForm(): void {
    if (!this.stepForm) {
      return;
    }

    const shipmentDetailsFields = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.SHIPMENT_DETAILS);
    const fromFields = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.FROM);
    const toFields = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.TO);
    const viaFields = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.VIA);

    this.sidebarFormRef = this.stepForm.createComponent(EsOcrQuickFixMainShipmentDetailsComponent, {
      injector: this.injector,
    });

    const shipmentDetailsFormRef: ComponentRef<EsOcrQuickFixMainShipmentDetailsComponent> = this.sidebarFormRef;

    shipmentDetailsFormRef.instance.shipmentDetailsFields = shipmentDetailsFields;
    shipmentDetailsFormRef.instance.fromFields = fromFields;
    shipmentDetailsFormRef.instance.toFields = toFields;
    shipmentDetailsFormRef.instance.viaFields = viaFields;

    shipmentDetailsFormRef.instance.dataSaved.pipe(take(1)).subscribe(() => {
      this.Next();
    });

    this.cdr.markForCheck();
  }

  public createShipmentTypeDetailsForm(): void {
    if (!this.stepForm) {
      return;
    }

    this.sidebarFormRef = this.stepForm.createComponent(EsOcrQuickFixShipmentTypeDetailsComponent, {
      injector: this.injector,
    });

    const shipmentTypeDetailsFormRef: ComponentRef<EsOcrQuickFixShipmentTypeDetailsComponent> = this.sidebarFormRef;
    const company = this.companiesService.getCompany();

    shipmentTypeDetailsFormRef.instance.entrySummary = this.entrySummary;
    shipmentTypeDetailsFormRef.instance.company = company;
    shipmentTypeDetailsFormRef.instance.dataSaved.pipe(take(1)).subscribe(() => {
      this.Next();
    });

    this.cdr.markForCheck();
  }

  public createBillOfLadingDetailsForm(): void {
    if (!this.stepForm) {
      return;
    }

    const masterScac = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.MASTER_SCAC);
    const masterBl = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.MASTER_BL);
    const houseScac = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.HOUSE_SCAC);
    const houseBl = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.HOUSE_BL);
    const quantity = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.QUANTITY);
    const it = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.IT);

    this.sidebarFormRef = this.stepForm.createComponent(EsOcrQuickFixBlDetailsComponent, {
      injector: this.injector,
    });

    const blDetailsFormRef: ComponentRef<EsOcrQuickFixBlDetailsComponent> = this.sidebarFormRef;
    const currentStep = this.esOcrQuickFixService.getCurrentStep();

    blDetailsFormRef.instance.masterScacFields = masterScac;
    blDetailsFormRef.instance.masterBlFields = masterBl;
    blDetailsFormRef.instance.houseScacFields = houseScac;
    blDetailsFormRef.instance.houseBlFields = houseBl;
    blDetailsFormRef.instance.quantityFields = quantity;
    blDetailsFormRef.instance.itFields = it;

    blDetailsFormRef.instance.isCanEdit = EntrySummaryActionsHelper.isCanEdit(this.entrySummary);
    blDetailsFormRef.instance.shipmentId = currentStep?.shipmentId;

    blDetailsFormRef.instance.dataSaved.pipe(take(1)).subscribe(() => {
      this.Next();
    });

    this.cdr.markForCheck();
  }

  public createSellerAndConsigneeDetailsForm(): void {
    if (!this.stepForm) {
      return;
    }
    const currentStep = this.esOcrQuickFixService.getCurrentStep();

    const sellerDetails = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.SELLER_DETAILS);
    const consigneeDetails = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.CONSIGNEE_DETAILS);
    const buyerDetails = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.BUYER_DETAILS);

    this.sidebarFormRef = this.stepForm.createComponent(EsOcrQuickFixSellerConsigneeDetailsComponent, {
      injector: this.injector,
    });

    const sellerConsigneeDetailsFormRef: ComponentRef<EsOcrQuickFixSellerConsigneeDetailsComponent> = this.sidebarFormRef;

    sellerConsigneeDetailsFormRef.instance.shipmentId = currentStep?.shipmentId;

    sellerConsigneeDetailsFormRef.instance.sellerFields = sellerDetails;
    sellerConsigneeDetailsFormRef.instance.consigneeFields = consigneeDetails;
    sellerConsigneeDetailsFormRef.instance.buyerFields = buyerDetails;

    sellerConsigneeDetailsFormRef.instance.dataSaved.pipe(take(1)).subscribe(() => {
      this.Next();
    });

    this.cdr.markForCheck();
  }

  public createShippingTermsOfSaleForm(): void {
    if (!this.stepForm) {
      return;
    }
    const currentStep = this.esOcrQuickFixService.getCurrentStep();

    const shippingTosFields = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.SHIPPING_TERMS_OF_SALE);

    this.sidebarFormRef = this.stepForm.createComponent(EsOcrQuickFixShippingTosComponent, {
      injector: this.injector,
    });

    const shippingTosFormRef: ComponentRef<EsOcrQuickFixShippingTosComponent> = this.sidebarFormRef;

    shippingTosFormRef.instance.shipmentId = currentStep?.shipmentId;

    shippingTosFormRef.instance.shippingTosFields = shippingTosFields;

    shippingTosFormRef.instance.dataSaved.pipe(take(1)).subscribe(() => {
      this.Next();
    });

    this.cdr.markForCheck();
  }

  public createInvoiceMainDetailsForm(): void {
    if (!this.stepForm) {
      return;
    }
    const currentStep = this.esOcrQuickFixService.getCurrentStep();

    const invoiceMainDetailsFields = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.INVOICE_MAIN_DETAILS);

    this.sidebarFormRef = this.stepForm.createComponent(EsOcrQuickFixInvoiceMainDetailsComponent, {
      injector: this.injector,
    });

    const invoiceMainDetailsFormRef: ComponentRef<EsOcrQuickFixInvoiceMainDetailsComponent> = this.sidebarFormRef;

    invoiceMainDetailsFormRef.instance.shipmentId = currentStep?.shipmentId;
    invoiceMainDetailsFormRef.instance.invoiceId = currentStep?.invoiceId;

    invoiceMainDetailsFormRef.instance.invoiceMainDetailsFields = invoiceMainDetailsFields;

    invoiceMainDetailsFormRef.instance.dataSaved.pipe(take(1)).subscribe(() => {
      this.Next();
    });

    this.cdr.markForCheck();
  }

  public createManufacturerDetailsForm(): void {
    if (!this.stepForm) {
      return;
    }

    const manufacturerDetails = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.MANUFACTURER_DETAILS);

    this.sidebarFormRef = this.stepForm.createComponent(EsOcrQuickFixManufacturerDetailsComponent, {
      injector: this.injector,
    });

    const currentStep = this.esOcrQuickFixService.getCurrentStep();

    const manufacturerConsigneeDetailsFormRef: ComponentRef<EsOcrQuickFixManufacturerDetailsComponent> = this.sidebarFormRef;

    manufacturerConsigneeDetailsFormRef.instance.shipmentId = currentStep?.shipmentId;
    manufacturerConsigneeDetailsFormRef.instance.invoiceId = currentStep?.invoiceId;

    manufacturerConsigneeDetailsFormRef.instance.manufacturerFields = manufacturerDetails;

    manufacturerConsigneeDetailsFormRef.instance.dataSaved.pipe(take(1)).subscribe(() => {
      this.Next();
    });

    this.cdr.markForCheck();
  }

  public createLineItemDetailsForm(isAddMode: boolean = false): void {
    if (!this.stepForm) {
      return;
    }

    const currentStep = this.esOcrQuickFixService.getCurrentStep();

    const lineItemDetails = this.esOcrQuickFixService.getSidebarSectionFields(enumEntrySummarySubStep.LINE_ITEM);

    this.sidebarFormRef = this.stepForm.createComponent(EsOcrQuickFixLineItemDetailsComponent, {
      injector: this.injector,
    });

    const lineItemDetailsFormRef: ComponentRef<EsOcrQuickFixLineItemDetailsComponent> = this.sidebarFormRef;

    lineItemDetailsFormRef.instance.shipmentId = currentStep?.shipmentId;
    lineItemDetailsFormRef.instance.invoiceId = currentStep?.invoiceId;
    lineItemDetailsFormRef.instance.itemId = currentStep?.lineItemId;
    lineItemDetailsFormRef.instance.isAddMode = isAddMode;

    lineItemDetailsFormRef.instance.lineItemFields = lineItemDetails;

    // #12863 — in add mode the step stays put after each save so more lines can be added; Next/Finish is handled by onNext.
    if (!isAddMode) {
      lineItemDetailsFormRef.instance.dataSaved.pipe(take(1)).subscribe(() => {
        this.Next();
      });
    }

    this.cdr.markForCheck();
  }

  public setIsOpenDocsPreviewModal(state: boolean) {
    this.isOpenDocsPreviewModal = state;
  }

  public get entryPreviewModalDocs(): DocumentPreviewModel[] {
    return this.previewModalDocs?.filter((pmd, index, self) => {
      return index === self.findIndex((p) => p.id === pmd.id);
    });
  }

  public get isFirstStep(): boolean {
    return this.currentStepIndex <= 0;
  }

  public get isLastStep(): boolean {
    const totalStepsCount = this.stepsToFix.length;
    return this.currentStepIndex + 1 >= totalStepsCount;
  }

  public get isNewLineItemStep(): boolean {
    return this.esOcrQuickFixService.getCurrentStep()?.type === enumEntrySummaryQuickFixStep.NEW_LINE_ITEM;
  }

  public get sidebarTitle(): string {
    const currentStepType = this.stepsToFix[this.currentStepIndex].type;
    const stepKeyName = enumEntrySummaryQuickFixStep[currentStepType];
    const stepName = `ENTRY_SUMMARY.OCR_QUICK_FIX_SIDEBAR.${[stepKeyName]}.TITLE`;

    switch (currentStepType) {
      case enumEntrySummaryQuickFixStep.BILL_OF_LADING_DETAILS:
      case enumEntrySummaryQuickFixStep.SELLER_AND_CONSIGNEE_DETAILS:
      case enumEntrySummaryQuickFixStep.SHIPPING_TERMS_OF_SALE:
        return this.getBlDetailsTitle(currentStepType);
      case enumEntrySummaryQuickFixStep.INVOICE_MAIN_DETAILS:
      case enumEntrySummaryQuickFixStep.MANUFACTURER:
      case enumEntrySummaryQuickFixStep.LINE_ITEM:
        return this.getInvoiceDetailsTitle(currentStepType);
      case enumEntrySummaryQuickFixStep.NEW_LINE_ITEM:
        // #12863 — the add step shares the line item sidebar titles (same BL / invoice wording)
        return this.getInvoiceDetailsTitle(enumEntrySummaryQuickFixStep.LINE_ITEM);
      default:
        return stepName;
    }
  }

  public getBlDetailsTitle(stepType: enumEntrySummaryQuickFixStep): string {
    const shipments = this.entrySummary?.shipments;
    const currentStepDuplicateNumber = this.currentStepDuplicateNumber;
    const stepKeyName = enumEntrySummaryQuickFixStep[stepType];

    let stepTranslationName: string;

    if (shipments?.length > 1) {
      stepTranslationName = `ENTRY_SUMMARY.OCR_QUICK_FIX_SIDEBAR.${[stepKeyName]}.TITLE_PLURAL`;

      const translatedBlDetailsTitle = this.translateService.instant(stepTranslationName, { number: currentStepDuplicateNumber });

      return translatedBlDetailsTitle;
    } else {
      stepTranslationName = `ENTRY_SUMMARY.OCR_QUICK_FIX_SIDEBAR.${[stepKeyName]}.TITLE`;

      return stepTranslationName;
    }
  }

  public getInvoiceDetailsTitle(stepType: enumEntrySummaryQuickFixStep): string {
    const currentStep = this.esOcrQuickFixService.getCurrentStep();
    const shipments = this.entrySummary?.shipments;
    const shipment = shipments?.find((shipment) => shipment.id === currentStep?.shipmentId);
    const invoice = shipment?.invoices?.find((invoice) => invoice.id === currentStep?.invoiceId);

    const currentShipmentIndex = shipments?.findIndex((sh) => sh.id === shipment.id);
    const currentInvoiceIndex = shipment?.invoices?.findIndex((inv) => inv.id === invoice.id);

    const stepKeyName = enumEntrySummaryQuickFixStep[stepType];

    let stepTranslationName: string;

    if (shipments?.length > 1 && shipment?.invoices?.length > 1) {
      stepTranslationName = `ENTRY_SUMMARY.OCR_QUICK_FIX_SIDEBAR.${[stepKeyName]}.TITLE_PLURAL_BL_INVOICE`;

      const translatedBlDetailsTitle = this.translateService.instant(stepTranslationName, {
        invoiceNumber: this.getNumberedIndex(currentInvoiceIndex),
        blNumber: this.getNumberedIndex(currentShipmentIndex),
      });

      return translatedBlDetailsTitle;
    } else if (shipments?.length > 1 && shipment?.invoices?.length <= 1) {
      stepTranslationName = `ENTRY_SUMMARY.OCR_QUICK_FIX_SIDEBAR.${[stepKeyName]}.TITLE_PLURAL_BL`;

      const translatedBlDetailsTitle = this.translateService.instant(stepTranslationName, {
        blNumber: this.getNumberedIndex(currentShipmentIndex),
      });

      return translatedBlDetailsTitle;
    } else if (shipments?.length <= 1 && shipment?.invoices?.length > 1) {
      stepTranslationName = `ENTRY_SUMMARY.OCR_QUICK_FIX_SIDEBAR.${[stepKeyName]}.TITLE_PLURAL_INVOICE`;

      const translatedBlDetailsTitle = this.translateService.instant(stepTranslationName, {
        invoiceNumber: this.getNumberedIndex(currentInvoiceIndex),
      });

      return translatedBlDetailsTitle;
    } else {
      stepTranslationName = `ENTRY_SUMMARY.OCR_QUICK_FIX_SIDEBAR.${[stepKeyName]}.TITLE`;

      return stepTranslationName;
    }
  }

  public get nextButtonText() {
    const currentStep = this.esOcrQuickFixService.getCurrentStep();
    const currentShipment = this.entrySummary.shipments.find((shipment) => shipment.id === currentStep?.shipmentId);
    const currentInvoice = currentShipment.invoices.find((invoice) => invoice.id === currentStep?.invoiceId);
    const currentLineItemIndex = currentInvoice?.lines?.findIndex((lineItem) => lineItem.id === currentStep?.lineItemId);

    // a line the classification assistant has not verified: Next opens the assistant first (#13403), so plain "Next"
    const isHtsValidationNeeded: boolean = this.isHtsValidationNeeded(currentStep);
    const isShowNextLineText =
      currentStep.type === enumEntrySummaryQuickFixStep.LINE_ITEM &&
      !isHtsValidationNeeded &&
      (currentInvoice?.lines || [])?.length > 1 &&
      currentLineItemIndex + 1 < (currentInvoice?.lines || [])?.length;
    return this.isLastStep ? 'FINISH' : isShowNextLineText ? 'NEXT_LINE' : 'LABELS.NEXT';
  }

  /**
   * Mirrors `EsOcrQuickFixLineItemDetailsComponent.isHtsValidationNeeded` from the store (the quick fix runs in importer
   * scope): the step component is not initialised yet when this label is first evaluated, so it cannot be asked.
   */
  private isHtsValidationNeeded(step: QuickFixStepModel): boolean {
    const entry: EntrySummaryModel | null = this.entrySummaryStateService.entrySummary$.getValue();
    const line: LineModel | null = step && step.lineItemId ? HtsLineStatusHelper.findLineModel(entry, step.lineItemId) : null;
    return (
      !!line &&
      this.companiesService.hasUserBetaFlag(enumUserBetaFlag.htsClassificationAssistant) &&
      EntrySummaryActionsHelper.isCanEdit(entry) &&
      HtsLineStatusHelper.isNeedsClassification(line)
    );
  }

  private Next() {
    const totalStepsCount = this.stepsToFix.length;

    if (this.currentStepIndex + 1 < totalStepsCount) {
      this.currentStepIndex++;
      this.setCurrentStepProgress(this.currentStepIndex);
      const updatedStep: QuickFixStepModel = this.stepsToFix[this.currentStepIndex];
      this.esOcrQuickFixService.setCurrentStep(updatedStep);

      this.syncStepForm();

      this.cdr.markForCheck();
    } else {
      this.esOcrQuickFixService.closeSidebar();
    }
  }

  private setSteps() {
    this.stepsToFix = this.esOcrQuickFixService.getSidebarSteps();
    const updatedStep: QuickFixStepModel = this.stepsToFix[this.currentStepIndex];
    this.esOcrQuickFixService.setCurrentStep(updatedStep);
  }

  private syncStepForm() {
    this.stepForm.remove();
    const currentStep = this.esOcrQuickFixService.getCurrentStep();
    const currentStepType = currentStep.type;
    switch (currentStepType) {
      case enumEntrySummaryQuickFixStep.MAIN_SHIPMENT_DETAILS:
        this.setDefaultPreviewDocument(enumDocumentType.INVOICE);
        this.createMainShipmentDetailsForm();
        break;
      case enumEntrySummaryQuickFixStep.SHIPMENT_TYPE_DETAILS:
        this.createShipmentTypeDetailsForm();
        break;
      case enumEntrySummaryQuickFixStep.BILL_OF_LADING_DETAILS:
        this.createBillOfLadingDetailsForm();
        break;
      case enumEntrySummaryQuickFixStep.SELLER_AND_CONSIGNEE_DETAILS:
        this.createSellerAndConsigneeDetailsForm();
        break;
      case enumEntrySummaryQuickFixStep.SHIPPING_TERMS_OF_SALE:
        this.createShippingTermsOfSaleForm();
        break;
      case enumEntrySummaryQuickFixStep.INVOICE_MAIN_DETAILS:
        this.createInvoiceMainDetailsForm();
        break;
      case enumEntrySummaryQuickFixStep.MANUFACTURER:
        this.createManufacturerDetailsForm();
        break;
      case enumEntrySummaryQuickFixStep.LINE_ITEM:
        this.createLineItemDetailsForm();
        break;
      case enumEntrySummaryQuickFixStep.NEW_LINE_ITEM:
        this.createLineItemDetailsForm(true);
        break;

      default:
        this.sidebarFormRef = null;
        break;
    }
  }

  private deleteDocumentFromServer(doc: DocumentModel, shipmentId: number): void {
    const isInvoiceUpload = doc.type === enumDocumentType.INVOICE;

    this.documentsService
      .deleteDocument(doc.id, false)
      .pipe(
        switchMap(() => this.entrySummaryStateService.updateDocuments(true)),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber).pipe(take(1))),
        finalize(() => {
          if (isInvoiceUpload) {
            this.entrySummaryStateService.invoiceCleared$.next(true);
            this.entrySummaryStateService.invoiceSaved$.next(false);
          }
          this.previewModalDocs = this.previewModalDocs.filter((pmd) => pmd.id !== doc.id);
          this.esOcrQuickFixService.setCurrentPreviewModalDocs(this.previewModalDocs);
          this.cdr.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((entry) => {
        this.entrySummaryStateService.updateCurrentEntrySummary(entry);
        this.cdr.detectChanges();
      });
  }

  private setDefaultPreviewDocument(type: enumDocumentType) {
    const firstDocumentWithType = this.entryPreviewModalDocs.findIndex((pmd) => pmd.type === type);
    this.currentPreviewModalDocIndex = firstDocumentWithType;
  }

  private setCurrentStepProgress(currentStepIndex: number) {
    const totalStepsCount = this.stepsToFix.length;
    const currentStepNumber = currentStepIndex + 1;
    const currentProgress = currentStepNumber / totalStepsCount;
    this.currentStepProgress = (currentProgress * 100 || 0) + '%';
  }

  private getNumberedIndex(index): string | null {
    return !!index || index === 0 ? (index + 1).toString() : null;
  }

  private get currentStepDuplicateNumber(): string | null {
    const currentStepType = this.stepsToFix[this.currentStepIndex].type;

    const currentStep = this.esOcrQuickFixService.getCurrentStep();
    const shipment = this.entrySummary?.shipments?.find((shipment) => shipment.id === currentStep?.shipmentId);
    const invoice = shipment?.invoices?.find((invoice) => invoice.id === currentStep?.invoiceId);

    switch (currentStepType) {
      case enumEntrySummaryQuickFixStep.BILL_OF_LADING_DETAILS:
      case enumEntrySummaryQuickFixStep.SELLER_AND_CONSIGNEE_DETAILS:
      case enumEntrySummaryQuickFixStep.SHIPPING_TERMS_OF_SALE:
        const currentShipmentIndex = this.entrySummary?.shipments?.findIndex((sh) => sh.id === shipment.id);
        return this.getNumberedIndex(currentShipmentIndex);
      default:
        return null;
    }
  }
}
